
import { useStytchMember } from "@stytch/react/b2b";
import moment from "moment-timezone";
import { useCreateKnowledgeReport, fetchKnowledgeItemsPage, KnowledgeApi, useGetCommonBlocks, useGetKnowledgeTags, useKnowledgeItemsPaginated, useGetKnowledgeTagsBreakdown, useListOwnersAssignedUsersTasks, ListOwnerAssignments } from "@/lib/api/knowledge";
import { cn, CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { ArrowLeft, CheckCircle, ChevronDown, ChevronRight, Clock, PenSquare, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import React, { useMemo, useState } from "react";
import { KnowledgeTaskCard } from "./components/KnowledgeTaskCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { Loader } from "@/components/ui/loader";
import { useQueryClient } from "@tanstack/react-query";
import { TagsManagementView } from "./components/TagsManagementView";
import { KnowledgeCommonBlockCard } from "./components/KnowledgeCommonBlockCard";
import { useToast } from "@/components/ui/use-toast";
import { downloadFromS3 } from "@/lib/file";
import { Separator } from "@/components/ui/separator";
import { PermissionType, PossiblePackage } from "@/lib/permissions/stytch_original";
import { hasAccessToFeature } from "@/lib/models/auth";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { KnowledgeSearchComponent } from "./components/KnowledgeSearchComponent";


export function AllTasksView({ currentPage, onPageChange }: { currentPage: number; onPageChange: (page: number) => void }) {
  const queryClient = useQueryClient();
  const [activeTagId, setActiveTagId] = useState<"no-tag" | string | null>(null);
  const [mainCategoryId, setMainCategoryId] = useState<string | null>(null);
  const [secondaryCategoryId, setSecondaryCategoryId] = useState<string | null>(null);
  const tagsBreakdownRes = useGetKnowledgeTagsBreakdown();
  const knowledgeItemsRes = useKnowledgeItemsPaginated({
    keyOverride: "all-tasks",
    params: {
      page: currentPage,
      filter: "all",
      activeTagId: undefined,
      emptyTags: activeTagId === "no-tag",
      main_category_id: mainCategoryId,
      secondary_category_id: secondaryCategoryId,
    },
    queryFn: fetchKnowledgeItemsPage,
    enabled: (!!mainCategoryId || !!secondaryCategoryId),
  });

  const tasks = (knowledgeItemsRes?.data?.data?.data || []);

  const tags = useMemo(() => {
    const base = tagsBreakdownRes?.data?.data?.tags || [];
    const mainIds = tagsBreakdownRes?.data?.data?.main_category_ids || [];
    const secondaryIdsBreakdown = tagsBreakdownRes?.data?.data?.main_to_secondary_id_breakdown || {};

    if (!mainCategoryId) {
      return base.filter((item: { id: string }) => mainIds.includes(item.id))
    }

    if (mainCategoryId && !secondaryCategoryId) {
      return base.filter((item: { id: string }) => ((secondaryIdsBreakdown || {})[mainCategoryId] || []).includes(item.id))
    }

    return []
  }, [tagsBreakdownRes, mainCategoryId, secondaryCategoryId]);

  const loadingOrErrorUi = GenerateLoadingOrError([activeTagId ? knowledgeItemsRes : undefined, tagsBreakdownRes].filter(Boolean));

  if (loadingOrErrorUi) {
    return (loadingOrErrorUi);
  }

  const handleTagSelect = (tagId: string) => {
    setActiveTagId(tagId);
    onPageChange(1);
  };

  const handleSelectMainCategory = (tagId: string | null) => {
    setMainCategoryId(tagId);
    onPageChange(1);
  };

  const handleSelectSubCategory = (tagId: string | null) => {
    setSecondaryCategoryId(tagId);
    onPageChange(1);
  };

  const handleClearTag = () => {
    setActiveTagId(null);
    onPageChange(1);
  }


  if (mainCategoryId || secondaryCategoryId) {
    return <CategoryTagDiveDown
      onSelectMain={handleSelectMainCategory} onSelectSecondary={handleSelectSubCategory} mainCategoryId={mainCategoryId} secondaryCategoryId={secondaryCategoryId} tags={tags}
      currentPage={currentPage}
      onPageChange={onPageChange}
      knowledgeItemsRes={knowledgeItemsRes}
      handleClearTag={handleClearTag}
      tasks={tasks}
    />
  }

  if (!activeTagId && activeTagId !== "no-tag") {
    return (
      <>
        <RenderTagList
          onSelect={handleSelectMainCategory}
          tags={tags} title="Drill down by Main Category"
          hasBack={false}
        />
        <Separator className="my-6" />
        <KnowledgeSearchComponent />
      </>
    )
  }

  return (<TagDiveDown tasks={tasks} currentPage={currentPage} onPageChange={onPageChange} knowledgeItemsRes={knowledgeItemsRes} handleClearTag={handleClearTag} />)
}

function CategoryTagDiveDown({
  handleClearTag, tasks, currentPage, onPageChange, knowledgeItemsRes, onSelectMain, onSelectSecondary, mainCategoryId, secondaryCategoryId, tags
}: {
  onSelectSecondary: (item: string | null) => any;
  onSelectMain: (item: string | null) => any;
  tags: KnowledgeApi.KnowledgeTaskTag[];
  mainCategoryId?: string | null;
  secondaryCategoryId?: string | null;
  currentPage: number;
  tasks: any,
  knowledgeItemsRes: any;
  onPageChange: (page: number) => void;
  handleClearTag: () => void;
}) {
  if (mainCategoryId && !secondaryCategoryId) {
    return (
      <>
        <RenderTagList onSelect={onSelectSecondary} tags={tags.filter((tag: KnowledgeApi.KnowledgeTaskTag) => tag.id !== mainCategoryId)} title="Drill down by Sub category" hasBack={true} onBack={() => onSelectMain(null)} />
        <TagDiveDown tasks={tasks} currentPage={currentPage} onPageChange={onPageChange} knowledgeItemsRes={knowledgeItemsRes} />
      </>
    );
  }

  return (<TagDiveDown tasks={tasks} currentPage={currentPage} onPageChange={onPageChange} knowledgeItemsRes={knowledgeItemsRes} handleClearTag={() => onSelectSecondary(null)} />)
}

function TagDiveDown({
  tasks,
  knowledgeItemsRes,
  currentPage,
  onPageChange,
  handleClearTag,
}: {
  currentPage: number;
  tasks: any,
  knowledgeItemsRes: any;
  onPageChange: (page: number) => void;
  handleClearTag?: () => void;
}) {
  if (!knowledgeItemsRes.isLoading && !knowledgeItemsRes.isFetching && !tasks?.length) {
    return (<div className="p-6 bg-background flex flex-col ">
      <div className="flex flex-row items-center justify-start">
        <Button variant="ghost" size="sm" className="w-[120px]" onClick={handleClearTag}><ArrowLeft className="h-4 w-4 mr-1" /> Clear Tag</Button>
        <h3 className="font-medium text-center ml-2">No tasks found for this tag</h3>
      </div>
    </div>)
  }

  const PaginationControls = () => {
    const isLoading = knowledgeItemsRes.isLoading || knowledgeItemsRes.isFetching;
    const info = knowledgeItemsRes?.data?.data?.pagination || {};

    return (
      <div className="flex items-center justify-between mt-4">
        <Button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1 || isLoading}
          className={cn(isLoading ? "flex items-center justify-center" : "")}
        >
          {isLoading ? <Loader className="w-4 h-4" /> : <span>Previous</span>}
        </Button>

        <span>
          Page {info.currentPage || 1} of {info.totalPages || 1}
        </span>

        <Button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= (info.totalPages || 1) || isLoading}
          className={cn(isLoading ? "flex items-center justify-center" : "")}
        >
          {isLoading ? <Loader className="w-4 h-4 px-2" /> : <span>Next</span>}
        </Button>
      </div>);
  };

  return (
    <>
      {handleClearTag && (<Button variant="ghost" size="sm" className="w-[120px]" onClick={handleClearTag}><ArrowLeft className="h-4 w-4 mr-1" /> Clear Tag</Button>)}
      {(knowledgeItemsRes.isLoading || knowledgeItemsRes.isFetching) && (
        <div className="flex items-center justify-center p-4">
          <Loader className="w-6 h-6" />
        </div>
      )}
      {
        !knowledgeItemsRes.isFetching && !knowledgeItemsRes.isLoading && tasks.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {tasks.map((task: KnowledgeApi.KnowledgeItem) => (
              <KnowledgeTaskCard
                key={`all-${task.id}`}
                task={task}
                isAssigned={false}
              />
            ))}
          </div>
        )}
      <PaginationControls />
    </>
  );
}

function RenderTagList({ onSelect, tags, title, hasBack, onBack }: { onSelect: (item: string | null) => any; tags: KnowledgeApi.KnowledgeTaskTag[]; title: string; hasBack?: boolean; onBack?: (item: any) => void }) {
  return (
    <div className="border rounded-lg p-6 bg-background text-center">
      <span className="flex flex-row justify-start items-center mb-3">
        {hasBack && (
          <Button onClick={onBack} variant={"ghost"} size="sm"><ArrowLeft className="w-4 h-4" /></Button>
        )}
        <h3 className="font-medium">{title}</h3>
      </span>
      <div className="flex flex-wrap gap-2 justify-start">
        {tags.map((tag: KnowledgeApi.KnowledgeTaskTag) => (
          <Button
            key={tag.id}
            variant="outline"
            size="sm"
            onClick={() => onSelect(tag.id)}
            className="px-3 py-1 h-auto"
          >
            {tag.name}
          </Button>
        ))}
        {tags.length === 0 && (
          <p className="text-sm text-muted-foreground">No tags available</p>
        )}
      </div>
    </div>
  );
}

function ViewTagCardsWithPagination() {

}
